import { useTranslation } from "react-i18next";
import { PMSimpleSelectorFieldV2 } from "../PMSimpleSelector/PMSimpleSelectorFieldV2";
import { useState, useEffect, useMemo, useCallback } from "react";
import { debounce } from "lodash";
import { getHseDocuments } from "../../../api/hse/documents";

/**
 * Searchable picker for HSE / KS documents (same UX as {@link ./reportSelector ReportSelector}).
 */
export const HseDocumentSelector = ({ showField = true, ...props }) => {
  const { setFieldValue, values, onChange, errors } = props;

  const name = props?.name ?? "hse_document_id";
  const label = props?.label ?? "resource_hub.faq.link_type.hse_document";

  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({
    id: null,
    field1: t("resource_hub.faq.linked_records_pick"),
    field2: null,
  });
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (props.open === true) setOpen(true);
    else if (props.open === false) setOpen(false);
  }, [props.open]);

  const handleChange = (data) => {
    const item = data?.selected?.item;
    if (item == null || item.id == null) {
      return;
    }

    setSelected({
      id: item.id,
      field1: item.primary,
      field2: item.secondary,
    });

    setOpen(false);
    props.onModalClose?.();

    if (values) {
      setFieldValue(name, item.id);
    }
    if (onChange) {
      onChange({
        target: {
          name,
          value: item.id,
          data: item,
          hse_document: item.object,
        },
      });
    }
  };

  useEffect(() => {
    if (props?.selected?.id != null && props?.selected?.id !== "") {
      setSelected({
        id: props.selected.id,
        field1: props.selected?.displayTitle || String(props.selected.id),
        field2: null,
      });
    } else {
      setSelected({
        id: null,
        field1: t("resource_hub.faq.linked_records_pick"),
        field2: null,
      });
    }
  }, [props.selected, t]);

  const [controller, setController] = useState({
    query: "",
    page: 1,
  });

  useEffect(() => {
    setController((prev) => ({ ...prev, query }));
  }, [query]);

  const loadDocs = useCallback(async (search) => {
    const q = search && search.trim() ? search.trim() : "";
    if (q.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const body = await getHseDocuments({
        search: q,
      });
      const docs = Array.isArray(body?.documents) ? body.documents : [];
      const mapped = docs.map((row) => ({
        value: row.id,
        id: row.id,
        primary: row.title || `#${row.id}`,
        secondary: row.full_document_number || row.document_number || null,
        object: row,
      }));
      setItems(mapped);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedLoad = useMemo(() => debounce((q) => loadDocs(q), 450), [loadDocs]);

  useEffect(() => {
    if (!open) return undefined;
    if (!controller.query || controller.query.trim().length < 2) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    debouncedLoad(controller.query);
    return () => debouncedLoad.cancel();
  }, [controller.query, open, debouncedLoad]);

  const handleToggleOpen = () => {
    setOpen((prev) => {
      if (prev) {
        props.onModalClose?.();
        return false;
      }
      return true;
    });
  };

  const labelAction = { action: () => {} };

  return (
    <PMSimpleSelectorFieldV2
      showField={showField}
      errors={errors}
      name={name}
      loading={loading}
      label={label}
      labelAction={labelAction}
      options={items}
      selected={selected}
      onChange={handleChange}
      onQueryChange={(q) => setQuery(q)}
      onClose={handleToggleOpen}
      open={open}
      listSecondaryLimit={40}
    />
  );
};
